import { cn } from "@/lib/utils";

/**
 * Active / Passive management-style badge.
 *
 * Single source of truth for the `isActiveManagement` pill — used identically on
 * the /products fund cards (FundCard) and the fund detail hero header. Reuses the
 * existing design-system classes `etf-fc-status-active` / `etf-fc-status-passive`
 * (see styles/etf-website.css) so light/dark themes stay in sync.
 *
 * `isActiveManagement` is an admin-written boolean on the DDB fund record.
 */
export function ActiveManagementBadge({
  isActiveManagement,
  className,
}: {
  isActiveManagement: boolean | undefined;
  className?: string;
}) {
  const active = Boolean(isActiveManagement);
  return (
    <span
      className={cn(
        "font-[family-name:var(--font-geist)] text-[11px] font-medium tracking-[0.01em] px-2.5 py-[3px] pl-2 rounded-full border border-solid inline-flex items-center gap-[5px] whitespace-nowrap normal-case",
        active
          ? "etf-fc-status-active text-[#86efac] border-[rgba(34,197,94,0.35)] bg-[rgba(34,197,94,0.12)]"
          : "etf-fc-status-passive text-[#93c5fd] border-[rgba(59,130,246,0.35)] bg-[rgba(59,130,246,0.12)]",
        className,
      )}
    >
      {active ? (
        <svg
          aria-hidden="true"
          focusable="false"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 opacity-85"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ) : (
        <svg
          aria-hidden="true"
          focusable="false"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0 opacity-85"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      )}
      {active ? "Active" : "Passive"}
    </span>
  );
}
